import moment from "moment";
import { useSearchParams } from "react-router-dom";
import SeparatedRowElements from "components/general/SeparatedRowElements";
import InfoRow from "components/general/InfoRow";
import Table from "components/general/Table";
import UserInfo from "components/cards/UserInfo";
import { AllStaffShiftsProps } from "apis/types/shift";
import { TableItemProps } from "components/general/Table/types";
import {
  useGetMyShiftsQuery,
  useGetMyEmployeeShiftsQuery,
} from "apis/services/shift";
import { ShiftType } from "constants/ShiftType";
import formatTime from "utils/formatDateTime";
import IMPORTANT_VARS from "constants/ImportantVars";
import useGetUserInfo from "hooks/useGetUserInfo";
import TabHeader from "./TabHeader";
import StaffAction from "./StaffAction";
import styles from "./styles.module.scss";
import ViewPunchesModal from "components/modals/ViewPunchesModal";
import usePermissions from "hooks/usePermission";
import { useState } from "react";
import Button from "components/general/Button";
import formateDate from "utils/formatDate";

export default function BasedSetup({
  statisticts,
  isLoading,
  viewStatistics,
}: {
  statisticts?: any;
  isLoading?: boolean;
  viewStatistics?: boolean;
}) {
  const [searchParams] = useSearchParams();
  const [isPunchesModalVisible, setIsPunchesModalVisible] = useState(false);
  const [selectedShift, setSelectedShift] =
    useState<AllStaffShiftsProps | null>(null);

  const { hasPermissionTo } = usePermissions();
  const canViewPunches = hasPermissionTo("view_punches");

  // const requestBasedOnRole =
  //   role === "employee" ? useGetMyShiftsQuery : useGetMyEmployeeShiftsQuery;

  const { data, isFetching: isEmployeeShiftsLoading } =
    useGetMyEmployeeShiftsQuery(
      {
        shift_type: ShiftType.Based,
        page: searchParams.get("page") ?? "1",
        search: searchParams.get("search") ?? "",
        // This tab shows the type filter too, so it has to send the param or
        // selecting and clearing it both leave the request untouched.
        type: searchParams.get("type") ?? "",
        start_date:
          searchParams.get("startDate") ??
          moment(new Date()).format("YYYY-MM-DD"),
        end_date: searchParams.get("endDate") ?? undefined,
        page_size: IMPORTANT_VARS.TabelDataPageSize,
        ordering: "date",
      },
      // Clearing a filter lands back on args that were already fetched once, so
      // without this the cached page is served and the list never re-asks.
      { refetchOnMountOrArgChange: true },
    );

  const INFOS = [
    {
      title: "Present Days",
      info: statisticts?.present ?? 0,
    },
    {
      title: "Off",
      info: statisticts?.off ?? 0,
    },
    {
      title: "Leaves",
      info: statisticts?.leaves ?? 0,
    },
    {
      title: "Absent",
      info: statisticts?.absent ?? 0,
    },
  ];

  const changeShiftData = (item: AllStaffShiftsProps): TableItemProps => ({
    id: String(item.id),
    rowData: [
      {
        type: "text",
        dataItem: <UserInfo data={item?.employee} isTableCol />,
      },
      { type: "text", dataItem: item?.specification ?? "-" },
      {
        type: "text",
        dataItem: formatTime(item.start_time) ?? "-",
      },
      {
        type: "text",
        dataItem: moment(item.date).format("DD-MM-YYYY") ?? "-",
      },
      {
        type: "text",
        dataItem: formateDate(
          item?.checkin?.checkin_time,
          "DD-MM-YYYY hh:mm A",
        ),
      },
      {
        type: "text",
        dataItem: formateDate(
          item?.checkin?.checkout_time,
          "DD-MM-YYYY hh:mm A",
        ),
      },
      {
        type: "text",
        // The column stays so the rows keep lining up with the headers, and
        // only the action goes when the punches endpoint is off limits.
        dataItem: canViewPunches ? (
          <Button
            type="button"
            isOutline
            onClick={() => {
              setSelectedShift(item);
              setIsPunchesModalVisible(true);
            }}
          >
            View Punches
          </Button>
        ) : (
          "-"
        ),
      },
      {
        type: "text",
        dataItem: `${item.working_hour?.number_of_hrs ?? "-"} Hours`,
      },
      {
        type: "element",
        dataItem: (
          <StaffAction
            isEditDisabled={moment(item.date).isBefore(moment())}
            shift={item}
            employee={item.employee}
            assignType="based"
          />
        ),
      },
    ],
  });

  return (
    <div className={styles.container}>
      <TabHeader shifType={ShiftType.Based} showRefreshBtn />

      <div className="my-4">
        {viewStatistics && (
          <SeparatedRowElements>
            {INFOS.map((item) => (
              <InfoRow
                key={item.title}
                title={item.title}
                info={`${item.info}`}
                isReverse
                infoFontSize={28}
                infoFontWeight="500"
              />
            ))}
          </SeparatedRowElements>
        )}
      </div>

      <div className="my-4">
        <Table
          isCheckBoxVisible
          showSearchFilter
          showTypeFilter
          headers={[
            "Members ID",
            "Status",
            "Starting Time",
            "Date",
            "Check In",
            "Check Out",
            "Punches",
            "Working Hours",
            "",
          ]}
          data={
            data?.results.map((item: AllStaffShiftsProps) =>
              changeShiftData(item),
            ) ?? []
          }
          isLoading={isEmployeeShiftsLoading || isLoading}
          count={data?.count}
          pageSize={IMPORTANT_VARS.TabelDataPageSize}
        />
      </div>

      <ViewPunchesModal
        isVisible={isPunchesModalVisible}
        setIsVisible={setIsPunchesModalVisible}
        shift={selectedShift}
      />
    </div>
  );
}
